<script setup lang="ts">
import { ref, onMounted, computed } from "vue";

import { useRouter, useRoute } from "vue-router";

import { useCrypto } from "@shared/crypto/useCrypto";

import { useChats, chats } from "@entities/chat/useChats";
import {
    useChatMessages,
    lastMessageByChat,
} from "@entities/message/useMessages";
import { peers, usePeers } from "@entities/peer/usePeers";

import { useFavorites } from "@features/favorites/useFavorites";

import ChatKnockModal from "@widgets/ChatKnock/ChatKnockModal.vue";
import ChatListHeader from "@widgets/ChatList/ChatListHeader.vue";
import ChatListItem from "@widgets/ChatList/ChatListItem.vue";

const router = useRouter();
const route = useRoute();
const { loadChats } = useChats();
const { announceOnline } = usePeers();
const { getLastMessage, decryptStored } = useChatMessages();
const { exportSignPublicKey } = useCrypto();
const loaded = ref(false);
// Свой ключ — чтобы отличать self-chat (peer === myKey) от обычных.
const myKey = ref<string | null>(null);

onMounted(async () => {
    myKey.value = await exportSignPublicKey();
    await loadChats();

    // Контент в IDB под ключом устройства — расшифровываем для превью
    // (чат-независимо, per-chat ключ не нужен).
    await Promise.all(
        chats.value.map(async (c) => {
            const last = await getLastMessage(c.id);
            if (last) {
                lastMessageByChat.value[c.id] = await decryptStored(last);
            }
        })
    );

    announceOnline();
    loaded.value = true;
});

const showNewChat = ref(false);
const { isFavorites, favoritesId, openFavorites } = useFavorites();

// «Избранное» — закреплено сверху отдельным пунктом.
const favoritesChat = computed(() =>
    chats.value.find((c) => isFavorites(c.id))
);
const favoritesActive = computed(
    () => route.params.chatId === favoritesId.value
);

// Обычные чаты: без текущего избранного и без осиротевших self-chat (их peer —
// это мой же ключ; появляются, если id избранного менялся). Их не показываем.
const activeChats = computed(() =>
    chats.value.filter(
        (c) =>
            c.established &&
            !isFavorites(c.id) &&
            peers.value[c.id]?.signPubKey !== myKey.value
    )
);

// Счётчик секции = обычные чаты + «Избранное» (оно тоже чат, просто внизу).
const chatCount = computed(
    () => activeChats.value.length + (favoritesChat.value ? 1 : 0)
);
</script>

<template>
    <div v-if="loaded" class="mc-chat-list">
        <ChatListHeader @new-chat="showNewChat = true" />

        <div v-if="chatCount" class="mc-chat-list__section">
            <span>ВСЕ ЧАТЫ</span>
            <span class="mc-chat-list__section-line" />
            <span>{{ chatCount }}</span>
        </div>

        <ChatListItem
            v-for="chat in activeChats"
            :key="chat.id"
            :chat="chat"
            :is-active="route.params.chatId === chat.id"
            @select="
                route.params.chatId === chat.id
                    ? router.push('/app')
                    : router.push(`/app/chat/${chat.id}`)
            "
        />

        <!-- «Избранное» — под всеми чатами. -->
        <ChatListItem
            v-if="favoritesChat"
            :chat="favoritesChat"
            :is-favorites="true"
            :is-active="favoritesActive"
            @select="openFavorites()"
        />
    </div>

    <ChatKnockModal v-if="showNewChat" @close="showNewChat = false" />
</template>

<style lang="scss" scoped>
.mc-chat-list {
    background-color: var(--mc-bg-list);
    border-right: 1px solid var(--mc-line);
    display: flex;
    flex-direction: column;

    &__section {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 14px 16px 8px;
        font-size: 0.625em;
        font-family: var(--mc-mono);
        font-weight: 700;
        color: var(--mc-fg-dim);
        letter-spacing: 0.06em;

        &-line {
            flex: 1;
            height: 1px;
            background: var(--mc-line-hard);
        }
    }
}
</style>
